import Swal from "sweetalert2";
import type { TFunction } from "i18next";

export type ClinicalGateAlert = {
  field_slug: string;
  label: string;
  answer_label: string;
  matched_service_ids?: number[];
  matched_product_ids?: number[];
};

const formatAlertsHtml = (alerts: ClinicalGateAlert[], t: TFunction): string => {
  if (alerts.length === 0) {
    return "";
  }

  const items = alerts
    .map((alert) => {
      const label = String(alert.label ?? "").trim();
      const answer = String(alert.answer_label ?? "").trim();
      if (!label) {
        return "";
      }

      return `<li><strong>${label}</strong>${
        answer ? ` — ${answer}` : ""
      }</li>`;
    })
    .filter(Boolean)
    .join("");

  if (!items) {
    return "";
  }

  return `<ul class="text-start mb-0 ps-3">${items}</ul>`;
};

/**
 * Clinical gate confirm (GAP-F2-120) — separate from preattend soft warn.
 * Returns whether staff may proceed.
 */
export async function confirmClinicalGateContinue(params: {
  t: TFunction;
  alerts?: ClinicalGateAlert[] | null;
  onOpenRecord?: () => void;
}): Promise<boolean> {
  const alerts = Array.isArray(params.alerts) ? params.alerts : [];
  const listHtml = formatAlertsHtml(alerts, params.t);

  const confirmation = await Swal.fire({
    title: params.t("studio.agenda.clinicalGate.title"),
    html: listHtml
      ? `${params.t("studio.agenda.clinicalGate.body")}<div class="mt-3">${listHtml}</div>`
      : params.t("studio.agenda.clinicalGate.body"),
    icon: "warning",
    showDenyButton: Boolean(params.onOpenRecord),
    showCancelButton: true,
    confirmButtonText: params.t("studio.agenda.clinicalGate.continue"),
    denyButtonText: params.t("studio.agenda.clinicalGate.openRecord"),
    cancelButtonText: params.t("studio.agenda.clinicalGate.cancel"),
    confirmButtonColor: "#f7b84b",
    denyButtonColor: "#0ab39c",
    cancelButtonColor: "#878a99",
    reverseButtons: true,
  });

  if (confirmation.isDenied && params.onOpenRecord) {
    params.onOpenRecord();
    return false;
  }

  return confirmation.isConfirmed;
}

export const isClinicalGateAckRequired = (error: unknown): boolean => {
  const code = (error as { code?: string } | null)?.code;

  return code === "CLINICAL_GATE_ACK_REQUIRED";
};

export const extractClinicalGateAlertsFromError = (
  error: unknown
): ClinicalGateAlert[] => {
  const details = (error as { details?: Record<string, unknown> } | null)?.details;
  const raw = details?.clinical_gate_alerts;

  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.filter(
    (item): item is ClinicalGateAlert =>
      Boolean(item) && typeof item === "object" && "label" in (item as object)
  );
};
